import { CustomStatusMapper } from '@csp/csp-common-custom-status';
import { ZonedDateTimeFactory, isDateToday, ZonedDateTimeFormatter, WindowInfo } from '@csp/csp-common-date-util';
import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  DisplayDateTimeStr,
  IsoDateStr,
  Maybe,
  TimeStr,
  VisitId,
  VisitLocation,
  VisitLocationType,
  VisitStatusType,
  VisitType,
  ZonedDateTime,
} from '@csp/csp-common-model';
import { RequestRef, ScheduleVersionRequestRef } from '@csp/csp-common-scheduling';
import { AppointmentV1 } from '@csp/dmdp-api-appointment-dto';
import { PatientVisitUtil } from '@csp/csp-common-patient-mapper';
import { VisitRequest } from '../schedule/model/VisitRequest';
import { Participant } from './Participant';
import { VisitConfirmationStatus } from './VisitConfirmationStatus';
import { VisitConfirmationStatusType } from './VisitConfirmationStatusType';
import { VisitCustomStatusType } from './VisitCustomStatusType';

const FOURTYEIGHT_HOURS_MILLIS = 48 * 3600 * 1000;

export type BookedVisit = {
  requestRef: RequestRef;
  templateCode?: string;
  templateId?: string;
  description?: string;
  location: VisitLocation;
  participants: Participant[];
  startTimestamp?: ZonedDateTime;
  startDateStr: IsoDateStr;
  startTimeStr: TimeStr;
  startDateTimeStr: DisplayDateTimeStr;
  status: VisitStatusType;
  // We take the title (visit name) from CMS
  // title: string;
  confirmationStatus: VisitConfirmationStatus;
  visitType: VisitType;
  visitId: VisitId;
  isFutureVisit: boolean;
  isPassedVisit: boolean;
  isVisitToday: boolean;
  isVisitStarted: boolean;
  isScheduledVisit: boolean;
  window: WindowInfo;
  createdAt?: ZonedDateTime;
};

const isVisitStarted = (startTimestamp: Maybe<ZonedDateTime>): boolean =>
  !!startTimestamp && startTimestamp.unixTimeMillis < Date.now();

const isVisitStartPassed = (startTimestamp: Maybe<ZonedDateTime>): boolean =>
  !PatientVisitUtil.isVisitStartInFuture(startTimestamp);

const from = ({
  appointmentId,
  description,
  location,
  participants,
  status,
  scheduleRequestRef,
  type,
  customStatuses,
  startTimestamp: startTimestampStr,
  audit,
}: AppointmentV1): BookedVisit => {
  const visitType: VisitType = EnumUtil.fromMaybeString(type, VisitType) ?? VisitType.UNKNOWN;
  const isScheduledVisit = visitType === VisitType.SCHEDULED;
  const startTimestamp = startTimestampStr
    ? ZonedDateTimeFactory.fromZonedDateTimeString(startTimestampStr)
    : undefined;
  const isFutureVisit = PatientVisitUtil.isVisitStartInFuture(startTimestamp);
  const isPassedVisit = !isFutureVisit;
  const isVisitToday = isDateToday(startTimestamp);

  const windowStart = scheduleRequestRef?.windowStart
    ? ZonedDateTimeFactory.fromZonedDateTimeString(scheduleRequestRef?.windowStart)
    : undefined;
  const windowEnd = scheduleRequestRef?.windowEnd
    ? ZonedDateTimeFactory.fromZonedDateTimeString(scheduleRequestRef?.windowEnd)
    : undefined;
  const window = WindowInfo.from(windowStart, windowEnd);

  return {
    visitId: appointmentId,
    description,
    // TODO: Fallback to { type: LocationTypeV1.PHONE } does not respect study protocol availableLocationFormats
    // config, use "UNKNOWN" instead?
    location: location ? VisitLocation.from(location) : { locationType: VisitLocationType.PHONE },
    participants: participants ?? [],
    startTimestamp,
    startDateStr: startTimestamp ? ZonedDateTimeFormatter.toIsoDateString(startTimestamp) : '-',
    startTimeStr: startTimestamp ? ZonedDateTimeFormatter.toTimeString(startTimestamp) || '-' : '-',
    startDateTimeStr: startTimestamp ? ZonedDateTimeFormatter.toDisplayDateTimeString(startTimestamp) : '-',
    status: status
      ? EnumUtil.fromStringOrDefault(status, VisitStatusType, VisitStatusType.UNKNOWN)
      : VisitStatusType.UNKNOWN,
    visitType,
    isFutureVisit,
    isPassedVisit,
    isVisitToday,
    isVisitStarted: isVisitStarted(startTimestamp),
    isScheduledVisit,
    confirmationStatus:
      CustomStatusMapper.fromCustomStatusesV1(customStatuses ?? []).getByType<VisitConfirmationStatusType>(
        VisitCustomStatusType.CONFIRMATION,
      ) ?? VisitConfirmationStatus.toUnknown(),
    requestRef: scheduleRequestRef
      ? {
          scheduleCode: scheduleRequestRef.scheduleCode,
          versionCode: scheduleRequestRef.versionCode,
          requestCode: scheduleRequestRef.requestCode,
          repeatIndex: scheduleRequestRef.repeatIndex,
          windowStart: scheduleRequestRef.windowStart
            ? ZonedDateTimeFactory.fromZonedDateTimeString(scheduleRequestRef.windowStart)
            : undefined,
          windowEnd: scheduleRequestRef.windowEnd
            ? ZonedDateTimeFactory.fromZonedDateTimeString(scheduleRequestRef.windowEnd)
            : undefined,
        }
      : ScheduleVersionRequestRef.fromGenericRequest(VisitRequest.toDefaultUnscheduledRequest([])),
    window,
    createdAt: audit?.createdAt ? ZonedDateTimeFactory.fromZonedDateTimeString(audit?.createdAt) : undefined,
  };
};

export const BookedVisit = {
  from,
  closeoutOverduePeriodMillis: FOURTYEIGHT_HOURS_MILLIS,
  isVisitStartPassed,
};
